import React, { useEffect, useMemo, useState } from 'react'
import '../styles/Variables.scss'
import Titre from '../components/Titre.jsx'
import dayjs from 'dayjs'
import { ConfigProvider, DatePicker, Flex, Spin, Table } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { api, apiPost } from '../functions/api.js'
import { useCookies } from 'react-cookie'
import isoWeek from 'dayjs/plugin/isoWeek'
import locale from 'antd/locale/fr_BE'
import 'dayjs/locale/fr.js'
import { toast } from 'react-toastify'
import AbsencesCheckbox from '../components/AbsencesCheckbox.jsx'
import '../styles/AbsencesEncodage.scss'

dayjs.locale('fr_BE')
dayjs.extend(isoWeek)

const dateFormat = 'DD/MM/YYYY'

const periodesParJours = {
  lundi: 6,
  mardi: 6,
  mercredi: 3,
  jeudi: 6,
  vendredi: 6,
}

const AbsencesEncodage = () => {
  const [cookies] = useCookies(['id', 'token', 'cours_fav'])

  // Données
  const [jeunes, setJeunes] = useState([])
  const [date, setDate] = useState(null)

  const [feries, setFeries] = useState([]) // ['YYYY-MM-DD', ...]
  const [vacances, setVacances] = useState([]) // [{ numero: <ISO week> }, ...]

  const [loading, setLoading] = useState(true)
  const [dataReady, setDataReady] = useState(false)

  // ⚠️ IMPORTANT: initialisé en tableau, pas en objet
  const [absencesTypes, setAbsencesTypes] = useState([])

  // Réponse API "absence/find/:date" typiquement: [{ eleve_id, code: [{periode, absId, checked}, ...]}, ...]
  // Mais on gère aussi un éventuel mapping objet pour être tolérant
  const [absencesData, setAbsencesData] = useState([])

  // ======================
  // Helpers de dates
  // ======================
  const isDateAvailable = (currentDate) => {
    if (!feries.length && !vacances.length) return false // on attend d'avoir chargé les congés
    const d = dayjs(currentDate)
    const dayOfWeek = d.day()
    const formattedDate = d.format('YYYY-MM-DD')
    const weekNumber = d.isoWeek()

    if (dayOfWeek === 0 || dayOfWeek === 6) return false // Weekends
    if (feries.includes(formattedDate)) return false // Fériés
    if (vacances.some((v) => v.numero === weekNumber)) return false // Semaines de vacances

    return true
  }

  const getNextAvailableDate = (startDate) => {
    let nextDate = dayjs(startDate, 'YYYY-MM-DD').add(1, 'day')
    // Sécurité anti boucle infinie (12 mois)
    for (let i = 0; i < 366 && !isDateAvailable(nextDate.format('YYYY-MM-DD')); i++) {
      nextDate = nextDate.add(1, 'day')
    }
    return nextDate.format('YYYY-MM-DD')
  }

  const getPreviousAvailableDate = (startDate) => {
    let prevDate = dayjs(startDate, 'YYYY-MM-DD').subtract(1, 'day')
    for (let i = 0; i < 366 && !isDateAvailable(prevDate.format('YYYY-MM-DD')); i++) {
      prevDate = prevDate.subtract(1, 'day')
    }
    return prevDate.format('YYYY-MM-DD')
  }

  const disableDates = (d) => {
    const dayOfWeek = dayjs(d).day()
    const formattedDate = dayjs(d).format('YYYY-MM-DD')
    const weekNumber = dayjs(d).isoWeek()

    if (dayOfWeek === 0 || dayOfWeek === 6) return true
    if (feries.includes(formattedDate)) return true
    if (vacances.some((v) => v.numero === weekNumber)) return true

    return false
  }

  // ======================
  // Fetchers
  // ======================
  const fetchConges = async () => {
    try {
      const response = await api('dates/conges', {
        id: cookies['id'],
        token: cookies['token'],
      })
      setVacances(response.data?.vacances || [])
      setFeries(response.data?.feries || [])
      setLoading(false)
    } catch (error) {
      console.error('Erreur lors de la récupération des congés :', error)
      setLoading(false)
    }
  }

  const fetchAbsencesTypes = async () => {
    try {
      const response = await api(`absencetype/all`, {
        id: cookies['id'],
        token: cookies['token'],
      })
      const data = response?.data ?? []
      const list = Array.isArray(data) ? data : Object.values(data)
      setAbsencesTypes(list)
    } catch (error) {
      console.error('Erreur lors de la récupération des absences types:', error)
      setAbsencesTypes([])
    }
  }

  const fetchJeunes = async () => {
    try {
      const response = await apiPost(
        `jeune/all/absences`,
        {
          id: cookies['id'],
          token: cookies['token'],
        },
        { date }
      )
      setJeunes(response?.data ?? [])
    } catch (error) {
      console.error('Erreur lors de la récupération des jeunes:', error)
      setJeunes([])
    }
  }

  const fetchAbsencesData = async () => {
    try {
      const response = await api(`absence/find/${date}`, {
        id: cookies['id'],
        token: cookies['token'],
      })

      const raw = response?.data ?? []
      // Normalise chaque entrée: parse "code" JSON si string
      const parsed = (Array.isArray(raw) ? raw : Object.values(raw)).map((absence) => ({
        ...absence,
        code: Array.isArray(absence.code)
          ? absence.code
          : absence.code
          ? (() => {
              try {
                return JSON.parse(absence.code)
              } catch {
                return []
              }
            })()
          : [],
      }))

      setAbsencesData(parsed)
    } catch (error) {
      console.error('Erreur lors de la récupération des absences:', error)
      setAbsencesData([])
    }
  }

  // ======================
  // Sauvegarde
  // ======================
  const saveCode = async (code) => {
    try {
      toast.dismiss()
      await toast.promise(
        apiPost(
          `absence/save/${date}`,
          {
            id: cookies['id'],
            token: cookies['token'],
          },
          code
        ),
        {
          pending: 'Envoi des données en cours...',
          success: 'Données sauvegardées avec succès 👌',
          error: {
            render({ data }) {
              return `Erreur lors de la sauvegarde : ${data?.response?.data?.message || 'Veuillez réessayer.'}`
            },
          },
        }
      )
      // On met à jour l'état local avec ce que le composant enfant renvoie
      setAbsencesData(code)
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des absences:', error)
    }
  }

  // ======================
  // Effets
  // ======================
  // Chargement de base: congés + types
  useEffect(() => {
    fetchConges()
    fetchAbsencesTypes()
  }, [])

  // Dès qu'on a les congés, on positionne la date au prochain jour valable
  useEffect(() => {
    if (!loading) {
      const today = dayjs().format('YYYY-MM-DD')
      setDate(isDateAvailable(today) ? today : getNextAvailableDate(today))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, feries, vacances])

  // Charge les jeunes + absences dès qu'on a une date
  useEffect(() => {
    const loadData = async () => {
      setDataReady(false)
      await Promise.all([fetchJeunes(), fetchAbsencesData()])
      setDataReady(true)
    }
    if (date) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date])

  // ======================
  // Dérivés pour rendu
  // ======================
  const periodesCount = useMemo(() => {
    if (!date) return 0
    switch (dayjs(date).day()) {
      case 1:
        return periodesParJours.lundi
      case 2:
        return periodesParJours.mardi
      case 3:
        return periodesParJours.mercredi
      case 4:
        return periodesParJours.jeudi
      case 5:
        return periodesParJours.vendredi
      default:
        return 0
    }
  }, [date])

  // Colonnes
  const columns = useMemo(() => {
    const base = [
      {
        title: 'Jeune',
        dataIndex: 'nom',
        key: 'nom',
        fixed: 'left',
      },
    ]

    if (!Array.isArray(absencesTypes) || absencesTypes.length === 0 || periodesCount === 0) {
      return base
    }

    let generated = [...base]
    for (let i = 0; i < periodesCount; i++) {
      const periodCols = absencesTypes.map((abs) => ({
        title: abs.intitule,
        dataIndex: `${abs.id}_${i}`,
        key: `${abs.id}_${i}`,
        className: `periode_${i}`,
      }))
      generated = [...generated, ...periodCols]
    }
    return generated
  }, [absencesTypes, periodesCount])

  // Récupère les absences d’un jeune sous forme de tableau [{periode, absId, checked}, ...]
  const getJeuneAbsencesArray = (jeuneId) => {
    // Cas 1: structure API [{eleve_id, code: [...]}, ...]
    if (Array.isArray(absencesData)) {
      const found = absencesData.find((a) => a.eleve_id === jeuneId)
      return Array.isArray(found?.code) ? found.code : []
    }
    // Cas 2: structure custom (mapping)
    const maybe = absencesData?.[jeuneId]
    if (!maybe) return []
    // On tente de le convertir en tableau uniforme
    // Ex: { [periode]: { [typeId]: true/false } }
    const arr = []
    Object.entries(maybe).forEach(([periode, typeMap]) => {
      Object.entries(typeMap || {}).forEach(([absId, checked]) => {
        if (checked) arr.push({ periode: Number(periode), absId: Number(absId), checked: true })
      })
    })
    return arr
  }

  // Lignes
  const rows = useMemo(() => {
    if (!dataReady || !Array.isArray(jeunes) || jeunes.length === 0) return []

    const types = Array.isArray(absencesTypes) ? absencesTypes : []

    return jeunes.map((jeune) => {
      const rowData = {
        nom: `${jeune.nom} ${jeune.prenom}`,
        key: jeune.id,
      }

      const jeuneAbsences = getJeuneAbsencesArray(jeune.id)

      for (let i = 0; i < periodesCount; i++) {
        types.forEach((abs) => {
          const key = `${abs.id}_${i}`
          const isChecked = jeuneAbsences.some((a) => a.periode === i && a.absId === abs.id && a.checked)

          // On confie la gestion du toggle au composant enfant
          rowData[key] = (
            <AbsencesCheckbox
              abs={abs.id}
              periode={i}
              jeune={jeune.id}
              code={absencesData ?? []}
              setCode={saveCode}
              date={date}
              periodesCount={periodesCount}
              defaultChecked={!!isChecked}
            />
          )
        })
      }

      return rowData
    })
  }, [dataReady, jeunes, absencesTypes, periodesCount, absencesData, date])

  // ======================
  // Render
  // ======================
  return (
    <div className="AbsencesEncodage" style={{ padding: '20px 60px' }}>
      <Titre>Les absences</Titre>
      <div className="Fond">
        {!dataReady || loading ? (
          <Spin />
        ) : (
          <>
            <Flex justify="space-between" align="center">
              <LeftOutlined
                style={{ fontSize: 20, cursor: 'pointer' }}
                onClick={() => setDate(getPreviousAvailableDate(date))}
              />
              <ConfigProvider locale={locale}>
                <DatePicker
                  value={date ? dayjs(date) : null}
                  format={dateFormat}
                  disabledDate={disableDates}
                  onChange={(e) => setDate(dayjs(e).format('YYYY-MM-DD'))}
                />
              </ConfigProvider>
              <RightOutlined
                style={{ fontSize: 20, cursor: 'pointer' }}
                onClick={() => setDate(getNextAvailableDate(date))}
              />
            </Flex>

            <Table
              dataSource={rows}
              columns={columns}
              pagination={false}
              scroll={{ x: true, y: 55 * 5 }}
              style={{ marginTop: 20, fontSize: 9 }}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default AbsencesEncodage
